<template>
    <Modal2
        :title="$t('message.selectFile')"
        :description="multiple ? 'Birden fazla dosya seçebilirsiniz' : 'Bir dosya seçin'"
        @cancel="handleCancel"
        @ok="handleConfirm"
        class-name="file-picker-modal"
    >
        <div class="file-picker">
            <div class="breadcrumb">
                <a-breadcrumb>
                    <a-breadcrumb-item v-for="folder in breadcrumbFolders" :key="folder._id">
                        <a href="#" @click.prevent="navigateToFolder(folder._id)">{{ folder.name }}</a>
                    </a-breadcrumb-item>
                </a-breadcrumb>
            </div>
            <div class="files-container">
                <FileCard
                    v-for="file in currentFiles"
                    :key="file._id"
                    :file="file"
                    :readonly="true"
                    :selectionMode="true"
                    :selected="isFileSelected(file)"
                    @click="handleDoubleClick"
                    @click.native="toggleFileSelection(file)"
                />
            </div>
        </div>
    </Modal2>
</template>

<script>
import Vue from "vue";
import FileCard from "./FileCard.vue";
import Modal2 from "../../../components/common/Modal2.vue";
import { DRIVE_API } from "../../../api/drive";

export default Vue.extend({
    name: "FilePicker",
    components: {
        Modal2,
        FileCard,
    },
    props: {
        multiple: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            folders: [],
            currentFolderId: null,
            currentFiles: [],
            breadcrumbFolders: [],
            selectedFiles: [],
        };
    },
    async created() {
        await this.fetchFolders();
        if (this.folders.length > 0) {
            const rootFolder = this.folders.find((f) => f.parent === null);
            if (rootFolder) {
                this.currentFolderId = rootFolder._id;
                await this.fetchCurrentFolderFiles();
            }
        }
    },
    methods: {
        async fetchFolders() {
            try {
                this.folders = await DRIVE_API.listAllFolders();
                if (!this.currentFolderId) this.showRootFolder();
                this.updateBreadcrumb();
            } catch (error) {
                console.error("Klasörler yüklenirken hata:", error);
            }
        },
        showRootFolder() {
            const root = this.folders.find((f) => f.parent === null);
            if (root) {
                this.currentFolderId = root._id;
            }
        },
        async fetchCurrentFolderFiles() {
            if (!this.currentFolderId) return;
            try {
                this.currentFiles = await DRIVE_API.listFilesByFolderId(this.currentFolderId);
            } catch (error) {
                console.error("Dosyalar yüklenirken hata:", error);
            }
        },
        updateBreadcrumb() {
            const currentFolder = this.folders.find((f) => f._id === this.currentFolderId);
            const breadcrumbFolders = [];

            let folder = currentFolder;
            while (folder) {
                breadcrumbFolders.unshift(folder);
                folder = this.folders.find((f) => f._id === folder.parent);
            }

            this.breadcrumbFolders = breadcrumbFolders;
        },
        navigateToFolder(folderId) {
            this.currentFolderId = folderId;
            this.selectedFiles = [];
        },
        handleDoubleClick(file) {
            if (file.isFolder) {
                this.navigateToFolder(file._id);
            }
        },
        toggleFileSelection(file) {
            if (file.isFolder) return;

            const index = this.selectedFiles.findIndex((f) => f._id === file._id);
            if (index !== -1) {
                this.selectedFiles.splice(index, 1);
            } else {
                if (this.multiple) {
                    this.selectedFiles.push(file);
                } else {
                    this.selectedFiles = [file];
                }
            }
        },
        isFileSelected(file) {
            return this.selectedFiles.some((f) => f._id === file._id);
        },
        handleCancel() {
            this.$emit("cancel");
        },
        handleConfirm() {
            if (this.selectedFiles.length > 0) {
                this.$emit("confirm", this.selectedFiles);
            }
        },
    },
    watch: {
        currentFolderId() {
            this.fetchCurrentFolderFiles();
            this.updateBreadcrumb();
        },
    },
});
</script>

<style scoped lang="scss">
.file-picker {
    display: flex;
    flex-direction: column;
    gap: 24px;
    min-height: 400px;

    .breadcrumb {
        margin-bottom: 16px;
        padding: 12px 16px;
        background: rgba(102, 126, 234, 0.05);
        border-radius: 12px;
        border: 1px solid rgba(102, 126, 234, 0.1);

        .ant-breadcrumb {
            &-separator {
                margin: 0 12px;
                color: #94a3b8;
                font-size: 14px;
            }

            &-item {
                a {
                    color: #667eea;
                    font-weight: 500;
                    text-decoration: none;
                    padding: 6px 12px;
                    border-radius: 8px;
                    transition: all 0.3s ease;

                    &:hover {
                        background: rgba(102, 126, 234, 0.1);
                        color: #764ba2;
                    }
                }
            }
        }
    }

    .files-container {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
        gap: 20px;
        padding: 16px;
        background: rgba(248, 250, 252, 0.5);
        border-radius: 12px;
        border: 2px dashed rgba(102, 126, 234, 0.2);
        min-height: 300px;

        .file-card {
            &.selected {
                border: 2px solid #667eea;
                background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
                transform: translateY(-2px);
                box-shadow: 0 8px 32px rgba(102, 126, 234, 0.2);
            }
        }
    }
}
</style>
<style lang="scss">
.file-picker-modal {
    .ant-modal {
        width: 900px !important;
        max-width: 95vw !important;
        max-height: 90vh !important;
        overflow: auto;
        border-radius: 16px !important;
        
        .ant-modal-content {
            border-radius: 16px !important;
            overflow: hidden;
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(10px);
            border: 1px solid rgba(255, 255, 255, 0.3);
        }

        .ant-modal-header {
            background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
            border-bottom: 1px solid rgba(0, 0, 0, 0.08);
            border-radius: 16px 16px 0 0 !important;
            padding: 20px 24px;

            .ant-modal-title {
                font-weight: 600;
                color: #1e293b;
                font-size: 18px;
            }
        }

        .ant-modal-body {
            padding: 24px;
        }

        .ant-modal-footer {
            border-top: 1px solid rgba(0, 0, 0, 0.08);
            padding: 16px 24px;
            background: rgba(248, 250, 252, 0.5);

            .ant-btn {
                border-radius: 8px;
                font-weight: 500;
                padding: 8px 16px;
                height: auto;

                &.ant-btn-primary {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    border: none;
                    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);

                    &:hover {
                        transform: translateY(-1px);
                        box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
                    }
                }
            }
        }
    }
}
</style>
